import { useMutation } from '@apollo/client';
import { captureException } from '@sentry/react';
import AuthLayout from '../Components/AuthLayout';
import RegistrationInvitedForm from '../Components/Forms/RegistrationInvitedForm';
import ResendVerification from '../Components/ResendVerification';
import useConfig from 'hooks/useConfig';
import useMessages from 'hooks/useMessages';
import {
  REGISTER_INVITED_USER_WITH_TOKEN,
  RESEND_INVITE_MAIL,
} from 'graphql/auth/mutations/auth.mutations';
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IRegisterUserValues, IRegisterValues } from 'FeatureModules/Auth/types/auth.types';
import { StringParam, useQueryParam } from 'use-query-params';
import ErrorState from 'Components/ErrorState';

const RegisterInvitedUser: FC = () => {
  const { appQueryParams } = useConfig();
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const [token] = useQueryParam(appQueryParams.registerToken, StringParam);
  const [email] = useQueryParam('email', StringParam);

  const [register] = useMutation<
    Record<string, unknown>,
    {
      token: string;
      input: IRegisterUserValues;
    }
  >(REGISTER_INVITED_USER_WITH_TOKEN, {
    fetchPolicy: 'no-cache',
  });

  const onSubmit = async (values: IRegisterValues) => {
    if (!token) {
      captureException('No token provided for registration.');
      return;
    }

    try {
      await register({
        variables: {
          token,
          input: {
            password: values.password,
            firstName: values.firstName,
            lastName: values.lastName,
            termsAccepted: values.termsAccepted,
          },
        },
      });
      setSuccessMessage('Registration successful! You can now log in with your new credentials.');
      navigate('/');
    } catch (error) {
      setHasError(true);
      if (error instanceof Error && error.cause === 'INVALID_INVITATION_LINK') {
        setErrorMessage('The invitation link is invalid or has expired.');
      } else {
        captureException(error);
        setErrorMessage(
          'An unknown error occurred during registration. Please try again or contact support.'
        );
      }
    }
  };

  return (
    <AuthLayout title="Register" isPartnerRegistration={false}>
      {!token && (
        <ErrorState
          error="Your invitation is not valid. Please contact your account administrator for a new invitation!"
          action={{
            onClick: () => navigate('/login'),
            label: 'Go to login',
          }}
        />
      )}

      {hasError && (
        <ResendVerification
          title="Verification link expired"
          subtitle="The link you used is expired. Use the button below to get a new one."
          email={email ?? ''}
          mutation={RESEND_INVITE_MAIL}
        />
      )}

      {token && !hasError && <RegistrationInvitedForm onSubmit={onSubmit} />}
    </AuthLayout>
  );
};

export default RegisterInvitedUser;
